import type { Action } from 'state/actions';
import { addKey, patchKey, removeKey, removeKeys } from 'state/utils/collection';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

import type { DashboardPinnedGroupColorChanged, DashboardPinnedGroupRenamed } from '../dashboard.actions';
import type { TileMetadata, TilesMap } from './tiles.model';

export function tilesReducer(state: TilesMap = {}, action: Action): TilesMap {
  switch (action.type) {
    case 'DASHBOARD_TILE_CREATED':
      return addKey(state, action.quoteId, { isPinned: false } as TileMetadata);
    case 'DASHBOARD_TILE_DUPLICATED':
      if (action.originalQuoteMetadata === undefined || action.originalQuoteMetadata === null) {
        getFrontLogger().warn(
          'missing metadata for quoteId {quoteId} on action {actionName}',
          action.quoteId,
          'DASHBOARD_TILE_DUPLICATED',
        );
        return state;
      }
      return addKey(state, action.quoteId, action.originalQuoteMetadata);
    case 'DASHBOARD_TILE_REMOVED':
      return removeKey(state, action.quoteId);
    case 'QUOTES_REMOVED':
    case 'DASHBOARD_OBSOLETE_QUOTES_DELETED':
      return removeKeys(state, action.quoteIds);
    case 'DASHBOARD_TILE_PIN_TOGGLED':
      return patchKey(state, action.quoteId, tile => ({
        ...tile,
        isPinned: !tile.isPinned,
        groupName: undefined,
        groupColor: undefined,
      }));
    case 'DASHBOARD_LOADED':
      return action.quoteIds.reduce((currentState, quoteId) => {
        const tileMetadata = action.metadata[quoteId];
        if (tileMetadata === undefined || tileMetadata === null) {
          getFrontLogger().warn(
            'missing metadata for quoteId {quoteId} on action {actionName}',
            quoteId,
            'DASHBOARD_LOADED',
          );
          return currentState;
        }
        return addKey(currentState, quoteId, tileMetadata);
      }, state);

    case 'DASHBOARD_TILE_GROUP_CHANGED':
      return patchKey(state, action.quoteId, tile => ({
        ...tile,
        groupName: action.groupName,
        isPinned: false,
        groupColor: action.color,
      }));
    case 'DASHBOARD_PINNED_GROUP_RENAMED':
      return onDashboardCustomGroupRenamed(state, action);
    case 'DASHBOARD_PINNED_GROUP_COLOR_CHANGED':
      return onDashboardPinnedGroupColorChanged(state, action);

    default:
      return state;
  }
}

function onDashboardCustomGroupRenamed(
  state: TilesMap,
  { previousName: previousGroupName, newName: newGroupName }: DashboardPinnedGroupRenamed,
): TilesMap {
  return Object.entries(state).reduce<TilesMap>((currentState, [quoteId, tile]) => {
    return {
      ...currentState,
      [quoteId]: tile.groupName === previousGroupName ? { ...tile, groupName: newGroupName } : tile,
    };
  }, {});
}

function onDashboardPinnedGroupColorChanged(
  state: TilesMap,
  { pinnedGroup: groupName, color }: DashboardPinnedGroupColorChanged,
): TilesMap {
  return Object.entries(state).reduce<TilesMap>((currentState, [quoteId, tile]) => {
    return {
      ...currentState,
      [quoteId]: tile.groupName === groupName ? { ...tile, groupColor: color } : tile,
    };
  }, {});
}
